import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Button,
    Typography,
    Box,
    Divider,
    FormControlLabel,
    Checkbox,
    Grid,
    Card,
    CardContent,
    Switch,
    Slider,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Alert,
    Paper,
    CircularProgress,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Close as CloseIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Description as DescriptionIcon,
    ViewColumn as ViewColumnIcon,
    Upload as UploadIcon,
    TextIncrease as TextIncreaseIcon,
    DesignServices as DesignServicesIcon,
    Add as AddIcon,
    Draw as DrawIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'react-signature-canvas';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import ClientService from '../../../Services/client.service.js';
import DocumentService from '../../../Services/documents.service.js';

const ReceiptCustomizationDialog = ({
    open,
    onClose,
    onSave,
    existingSignature,
    existingProfilePicture,
    existingDescription,
    existingFooter,
    columnSettings,
    token,
    accountantId,
    caseId,
    direction,
    formData, // Add formData prop to generate PDF
    client, // Add client prop
    invoiceModalType // Add invoiceModalType prop
}) => {
    const { t } = useTranslation();
    
    // State for signature and profile picture
    const [signaturePreview, setSignaturePreview] = useState(existingSignature || '');
    const [profilePreview, setProfilePreview] = useState(existingProfilePicture || '');
    const [newSignatureFile, setNewSignatureFile] = useState(null);
    const [newProfileFile, setNewProfileFile] = useState(null);
    const [addSignaturePad, setAddSignaturePad] = useState(false);
    const [textSignatureOption, setTextSignatureOption] = useState(false);
    const [textSignature, setTextSignature] = useState('');
    const [selectedFont, setSelectedFont] = useState('Arial');
    
    // State for descriptions
    const [description, setDescription] = useState(existingDescription || '');
    const [footer, setFooter] = useState(existingFooter || '');
    
    // State for column settings
    const [columns, setColumns] = useState(columnSettings || {
        textSize: 11,
        profilePictureShape: "circle",
        boldDate: false
    });
    
    // Zoom states
    const [signatureZoomOpen, setSignatureZoomOpen] = useState(false);
    const [profileZoomOpen, setProfileZoomOpen] = useState(false);
    
    // PDF preview state
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [previewDebounceTimer, setPreviewDebounceTimer] = useState(null);

    const sigPad = useRef();
    
    const fonts = [
        'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana', 'Pacifico', 'Dancing Script'
    ];

    // Create a function to prepare receipt data
    const prepareReceiptData = useCallback((customColumnSettings = null) => {
        if (!formData || !client) return null;
        
        // Use custom column settings if provided, otherwise use current state
        const currentColumnSettings = customColumnSettings || columns;
        
        return {
            CaseId: client.caseId,
            AccountantId: client.accountantId,
            Name: client.name,
            Description: description || formData.invoiceDescription,
            Reference: formData.invoiceReference || "",
            ClientName: formData.invoiceClientName,
            ClientCaseId: formData.invoiceClientCaseId || "",
            PhoneNumber: client.phoneNumber,
            SecondaryPhoneNumber: client.secondaryPhoneNumber,
            Email: client.email,
            Website: client.website,
            Location: client.location,
            ClientLocation: formData.invoiceClientLocation,
            ClientEmail: formData.invoiceClientEmail,
            ClientPhoneNumber: formData.invoiceClientPhoneNumber,
            Date: formData.invoiceDate,
            reportingMonthDate: new Date(),
            Signature: signaturePreview,
            AccountCodeId: formData.invoiceAccountCodeId?.toString(),
            Items: formData.invoiceItems || [],
            PriceIncludingVAT: formData.priceIncludingVAT,
            ForceNoTax: formData.forceNoTax,
            DeferredReceipt: formData.deferredReceipt,
            ForeignCurrencyCode: formData.foreignCurrencyCode,
            // Receipt-specific fields
            IsInvoiceReceipt: invoiceModalType === 5,
            Title: "Receipt",
            checks: formData.receiptItems?.checks || [],
            CreditCards: formData.receiptItems?.creditCards || [],
            Cash: formData.receiptItems?.cash || [],
            BankTransactions: formData.receiptItems?.bankTransactions || [],
            WithholdingTax: parseFloat(formData.invoiceWithholdingTax) || 0,
            // Customization fields
            CustomDescription: description,
            CustomFooter: footer,
            ColumnSettings: {
                textSize: currentColumnSettings.textSize,
                profilePictureShape: currentColumnSettings.profilePictureShape,
                boldDate: currentColumnSettings.boldDate
            }
        };
    }, [formData, client, description, signaturePreview, footer, columns, invoiceModalType]);

    useEffect(() => {
        setSignaturePreview(existingSignature || '');
        setProfilePreview(existingProfilePicture || '');
        setDescription(existingDescription || '');
        setFooter(existingFooter || '');
        setColumns(columnSettings || {
            textSize: 11,
            profilePictureShape: "circle",
            boldDate: false
        });
    }, [existingSignature, existingProfilePicture, existingDescription, existingFooter, columnSettings]);

    // Generate PDF preview when customization changes
    useEffect(() => {
        if (open && formData && client) {
            generatePdfPreview();
        }
    }, [open, formData, client]);

    const generatePdfPreview = async () => {
        const receiptData = prepareReceiptData(columns);
        if (!receiptData) {
            console.error('Failed to prepare receipt data');
            return;
        }
        
        setPdfLoading(true);
        try {
            // Call the PDF generation service
            const pdfBlob = await DocumentService.CreateDraftInvoice(
                "PaymentAcceptances",
                "GeneratePaymentAcceptanceDocumentDraftPdf",
                receiptData,
                "PaymentAcceptance",
                "Receipt",
                "Receipt",
                token
            );

            const pdfUrl = URL.createObjectURL(pdfBlob);
            setPdfPreview(pdfUrl);
        } catch (error) {
            console.error('Error generating PDF preview:', error);
            toast.error(t('ErrorGeneratingPDFPreview') || 'Error generating PDF preview');
        } finally {
            setPdfLoading(false);
        }
    };

    // Debounced function to update preview
    const updatePreview = useCallback((data) => {
        if (previewDebounceTimer) {
            clearTimeout(previewDebounceTimer);
        }

        const timer = setTimeout(async () => {
            try {
                setPdfLoading(true);
                
                // Use the new column settings if provided, otherwise use current state
                const newColumnSettings = data.columns || {
                    textSize: columns.textSize,
                    profilePictureShape: columns.profilePictureShape,
                    boldDate: columns.boldDate
                };

                // Prepare the data for preview generation with updated settings
                const receiptData = prepareReceiptData(newColumnSettings);
                if (!receiptData) {
                    console.error('Failed to prepare receipt data for preview');
                    return;
                }

                const previewData = {
                    ...receiptData,
                    Signature: data.signature || signaturePreview,
                    CustomDescription: data.description || description,
                    CustomFooter: data.footer || footer,
                    ColumnSettings: newColumnSettings
                };

                // Call the PDF generation service for preview
                const pdfBlob = await DocumentService.CreateDraftInvoice(
                    "PaymentAcceptances",
                    "GeneratePaymentAcceptanceDocumentDraftPdf",
                    previewData,
                    "PaymentAcceptance",
                    "Receipt",
                    "Receipt",
                    token
                );

                const pdfUrl = URL.createObjectURL(pdfBlob);
                setPdfPreview(pdfUrl);
            } catch (error) {
                console.error('Error updating preview:', error);
            } finally {
                setPdfLoading(false);
            }
        }, 500); // 500ms debounce

        setPreviewDebounceTimer(timer);
    }, [previewDebounceTimer, prepareReceiptData, signaturePreview, description, footer, columns, token]);

    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setSignaturePreview(objectUrl);
            setNewSignatureFile(file);
            // Trigger preview update
            updatePreview({ signature: objectUrl });
        }
    };

    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setProfilePreview(objectUrl);
            setNewProfileFile(file);
            // Trigger preview update
            updatePreview({ profilePicture: objectUrl });
        }
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (previewDebounceTimer) {
                clearTimeout(previewDebounceTimer);
            }
        };
    }, [previewDebounceTimer]);

    const handleSignatureChange = () => {
        if (sigPad.current) {
            const signatureDataUrl = sigPad.current.toDataURL();
            setSignaturePreview(signatureDataUrl);
            setNewSignatureFile(null);
            // Trigger preview update
            updatePreview({ signature: signatureDataUrl });
        }
    };

    const handleProfilePictureChange = (newProfilePicture) => {
        setProfilePreview(newProfilePicture);
        setNewProfileFile(null);
        // Trigger preview update
        updatePreview({ profilePicture: newProfilePicture });
    };

    const handleDescriptionChange = (event) => {
        const newDescription = event.target.value;
        setDescription(newDescription);
        // Trigger preview update
        updatePreview({ description: newDescription });
    };

    const handleFooterChange = (event) => {
        const newFooter = event.target.value;
        setFooter(newFooter);
        // Trigger preview update
        updatePreview({ footer: newFooter });
    };



    const handleClearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
        }
        setSignaturePreview('');
        setNewSignatureFile(null);
        setTextSignature('');
        // Trigger preview update
        updatePreview({ signature: '' });
    };

    const generateTextSignatureImage = async () => {
        if (!textSignature.trim()) {
            toast.error(t('PleaseEnterTextForSignature') || 'Please enter text for signature');
            return;
        }

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 300;
            canvas.height = 100;

            // Set font
            ctx.font = `24px ${selectedFont}`;
            ctx.fillStyle = '#000000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Draw text
            ctx.fillText(textSignature, canvas.width / 2, canvas.height / 2);

            const dataUrl = canvas.toDataURL();
            setSignaturePreview(dataUrl);
            setNewSignatureFile(null);
            
            // Trigger preview update
            updatePreview({ signature: dataUrl });
            
            toast.success(t('TextSignatureGenerated') || 'Text signature generated successfully');
        } catch (error) {
            console.error('Error generating text signature:', error);
            toast.error(t('ErrorGeneratingTextSignature') || 'Error generating text signature');
        }
    };

    const handleColumnChange = (columnKey, field, value) => {
        setColumns(prevColumns => {
            const newColumns = {
                ...prevColumns,
                [columnKey]: value
            };
            
            // Trigger preview update with new column settings
            updatePreview({ columns: newColumns });
            
            return newColumns;
        });
    };

    const handleSave = async () => {
        try {
            // Upload signature if there's a new one
            if (newSignatureFile) {
                let fileToUpload;
                if (typeof newSignatureFile === 'string' && newSignatureFile.startsWith('data:image/')) {
                    const base64Data = newSignatureFile.split(',')[1];
                    const binaryData = atob(base64Data);
                    const arrayBuffer = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) {
                        arrayBuffer[i] = binaryData.charCodeAt(i);
                    }
                    const blob = new Blob([arrayBuffer], { type: 'image/png' });
                    fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                } else {
                    fileToUpload = newSignatureFile;
                }

                const response = await ClientService.UploadSignature(token, accountantId, caseId, fileToUpload);
                if (response.ok) {
                    toast.success(t('SignatureUploadedSuccessfully'));
                } else {
                    toast.error(t('ErrorUploadingSignatureFiles'));
                }
            }

            // Upload profile picture if there's a new one
            if (newProfileFile) {
                const response = await ClientService.UploadProfilePhoto(
                    token,
                    accountantId,
                    caseId,
                    newProfileFile
                );
                if (response.ok) {
                    toast.success(t('ProfilePhotoUploadedSuccessfully'));
                } else {
                    toast.error(t('ErrorUploadingProfileFiles'));
                }
            }

            const customizationData = {
                signature: newSignatureFile || existingSignature,
                profilePicture: newProfileFile || existingProfilePicture,
                description,
                footer,
                columns: {
                    textSize: columns.textSize,
                    profilePictureShape: columns.profilePictureShape,
                    boldDate: columns.boldDate
                },
                hasChanges: !!(newSignatureFile || newProfileFile || 
                              description !== existingDescription || 
                              footer !== existingFooter ||
                              JSON.stringify(columns) !== JSON.stringify(columnSettings))
            };
            
            onSave(customizationData);
        } catch (error) {
            console.error('Error saving customization:', error);
            toast.error(t('ErrorSavingCustomization'));
        }
    };

    const renderSignatureSection = () => (
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <CreateIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('SignatureAndProfilePicture')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {/* Signature Section */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('Signature')}
                                </Typography>
                                
                                {signaturePreview && (
                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <img
                                            src={signaturePreview}
                                            alt="signature-preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100px',
                                                border: '1px solid #ccc',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setSignatureZoomOpen(true)}
                                        />
                                    </Box>
                                )}

                                {addSignaturePad && (
                                    <Box sx={{ mb: 2 }}>
                                        <SignaturePad
                                            ref={sigPad}
                                            canvasProps={{
                                                style: { 
                                                    border: '1px solid #ccc', 
                                                    width: '100%', 
                                                    height: '150px' 
                                                },
                                            }}
                                            onEnd={handleSignatureChange}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={handleClearSignature}
                                            sx={{ mt: 1 }}
                                        >
                                            {t('ClearSignature')}
                                        </Button>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        size="small"
                                        startIcon={<UploadIcon />}
                                    >
                                        {t('UploadSignature')}
                                        <input
                                            type="file"
                                            hidden
                                            onChange={handleSignatureUpload}
                                        />
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<DrawIcon />}
                                        onClick={() => setAddSignaturePad(!addSignaturePad)}
                                    >
                                        {t('DrawSignature')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<TextIncreaseIcon />}
                                        onClick={() => setTextSignatureOption(!textSignatureOption)}
                                    >
                                        {t('TextSignature')}
                                    </Button>
                                </Box>

                                {textSignatureOption && (
                                    <Box sx={{ mt: 2 }}>
                                        <TextField
                                            select
                                            label={t('Font')}
                                            value={selectedFont}
                                            onChange={(e) => setSelectedFont(e.target.value)}
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 1 }}
                                        >
                                            {fonts.map((font) => (
                                                <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                                                    {font}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <ReactQuill
                                            theme="snow"
                                            value={textSignature}
                                            onChange={setTextSignature}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline'],
                                                    [{ 'align': [] }],
                                                ],
                                            }}
                                            style={{ height: '100px', marginBottom: '10px' }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={generateTextSignatureImage}
                                            fullWidth
                                        >
                                            {t('GenerateTextSignature')}
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Profile Picture Section */}
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('ProfilePicture')}
                                </Typography>
                                
                                {profilePreview && (
                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <img
                                            src={profilePreview}
                                            alt="profile-preview"
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100px',
                                                border: '1px solid #ccc',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setProfileZoomOpen(true)}
                                        />
                                    </Box>
                                )}

                                <Button
                                    variant="contained"
                                    component="label"
                                    fullWidth
                                    startIcon={<UploadIcon />}
                                >
                                    {t('UploadProfilePicture')}
                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleProfilePictureUpload}
                                    />
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );

    const renderColumnSettingsSection = () => (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <ViewColumnIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('DocumentSettings') || 'Document Settings'}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3}>
                    {/* Text Size Setting */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('TextSize') || 'Text Size'}
                        </Typography>
                        <Slider
                            value={columns.textSize}
                            onChange={(e, newValue) => handleColumnChange('textSize', null, newValue)}
                            min={8}
                            max={16}
                            step={1}
                            marks
                            valueLabelDisplay="auto"
                            sx={{ mt: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {columns.textSize}px
                        </Typography>
                    </Grid>

                    {/* Profile Picture Shape */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('ProfilePictureShape') || 'Profile Picture Shape'}
                        </Typography>
                        <Select
                            value={columns.profilePictureShape}
                            onChange={(e) => handleColumnChange('profilePictureShape', null, e.target.value)}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="circle">{t('Circle') || 'Circle'}</MenuItem>
                            <MenuItem value="rectangle">{t('Rectangle') || 'Rectangle'}</MenuItem>
                            <MenuItem value="hexagon">{t('Hexagon') || 'Hexagon'}</MenuItem>
                            <MenuItem value="diamond">{t('Diamond') || 'Diamond'}</MenuItem>
                            <MenuItem value="roundedRectangle">{t('RoundedRectangle') || 'Rounded Rectangle'}</MenuItem>
                            <MenuItem value="oval">{t('Oval') || 'Oval'}</MenuItem>
                            <MenuItem value="triangle">{t('Triangle') || 'Triangle'}</MenuItem>
                        </Select>
                    </Grid>

                    {/* Bold Date */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={columns.boldDate}
                                    onChange={(e) => handleColumnChange('boldDate', null, e.target.checked)}
                                />
                            }
                            label={t('BoldDate') || 'Bold Date'}
                        />
                    </Grid>


                </Grid>
            </AccordionDetails>
        </Accordion>
    );

    const renderDescriptionSection = () => (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">{t('DescriptionAndFooter')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label={t('InvoiceDescription')}
                            multiline
                            rows={4}
                            value={description}
                            onChange={handleDescriptionChange}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t('InvoiceFooter')}
                            multiline
                            rows={4}
                            value={footer}
                            onChange={handleFooterChange}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                sx={{ direction }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SettingsIcon sx={{ mr: 1 }} />
                        <Typography variant="h5">{t('ReceiptCustomization') || 'Receipt Customization'}</Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ mb: 2, p: 2 }}>
                        <Alert severity="info">
                            {t('ReceiptCustomizationInfo') || 'Customize your receipt with signature, profile picture, descriptions, and column settings. Changes will be reflected in the live preview.'}
                        </Alert>
                    </Box>
                    
                    {/* Main Content - Two Column Layout */}
                    <Box sx={{ display: 'flex', height: '70vh', gap: 2 }}>
                        {/* Left Panel - Customization Options */}
                        <Box sx={{ 
                            width: '50%', 
                            p: 2, 
                            overflowY: 'auto',
                            borderRight: '1px solid #e0e0e0'
                        }}>
                            {renderSignatureSection()}
                            {renderColumnSettingsSection()}
                            {renderDescriptionSection()}
                        </Box>

                        {/* Right Panel - PDF Preview */}
                        <Box sx={{ 
                            width: '50%', 
                            p: 2, 
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Typography variant="h6" gutterBottom>
                                {t('LivePreview') || 'Live Preview'}
                            </Typography>
                            
                            {pdfLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <CircularProgress />
                                </Box>
                            ) : pdfPreview ? (
                                <Box sx={{ flex: 1, overflow: 'auto' }}>
                                    <iframe
                                        src={pdfPreview}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px'
                                        }}
                                        title="PDF Preview"
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    flex: 1,
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    border: '1px dashed #e0e0e0',
                                    borderRadius: '4px'
                                }}>
                                    <Typography color="text.secondary">
                                        {t('PDFPreviewWillAppearHere') || 'PDF preview will appear here'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined">
                        {t('Cancel')}
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        startIcon={<SaveIcon />}
                    >
                        {t('SaveAndGeneratePDF') }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Signature Zoom Dialog */}
            <Dialog
                open={signatureZoomOpen}
                onClose={() => setSignatureZoomOpen(false)}
                maxWidth="lg"
            >
                <DialogTitle>
                    {t('Signature')}
                    <IconButton
                        onClick={() => setSignatureZoomOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {signaturePreview && (
                        <img
                            src={signaturePreview}
                            alt="signature-zoom"
                            style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Profile Zoom Dialog */}
            <Dialog
                open={profileZoomOpen}
                onClose={() => setProfileZoomOpen(false)}
                maxWidth="lg"
            >
                <DialogTitle>
                    {t('ProfilePicture')}
                    <IconButton
                        onClick={() => setProfileZoomOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {profilePreview && (
                        <img
                            src={profilePreview}
                            alt="profile-zoom"
                            style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ReceiptCustomizationDialog;
